import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { UpstreamServiceOutagesManagementBoard, SubscriptionsManagementBoard } from '@api/BoardSDK';

export const getIncidents = createServerFn({ method: 'GET' })
  .validator(z.object({}).optional())
  .handler(async () => {
    const board = new UpstreamServiceOutagesManagementBoard();
    
    // Get incidents from last 6 months (180 days)
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setDate(sixMonthsAgo.getDate() - 180);
    const sixMonthsAgoStr = sixMonthsAgo.toISOString().split('T')[0];
    
    const res = await board.items()
      .withColumns(['status', 'serviceStatus', 'serviceType', 'subServiceType', 'incidentStart', 'incidentEnd'])
      .withUpdates()
      .where({
        incidentStart: { gte: sixMonthsAgoStr }
      })
      .orderBy({ column: 'incidentStart', direction: 'desc' })
      .withPagination({ limit: 500 })
      .execute();
    
    return {
      items: res.items ?? [],
      cursor: res.cursor
    };
  });

export const getIncidentDetails = createServerFn({ method: 'GET' })
  .validator(z.object({ itemId: z.string() }))
  .handler(async ({ data }) => {
    const board = new UpstreamServiceOutagesManagementBoard();
    
    const item = await board.item(data.itemId)
      .withColumns(['status', 'serviceStatus', 'serviceType', 'subServiceType', 'incidentStart', 'incidentEnd'])
      .withUpdates()
      .execute();
    
    return item;
  });

export const createSubscription = createServerFn({ method: 'POST' })
  .validator(z.object({
    firstName: z.string().min(1),
    email: z.string().email().optional(),
    phoneNumber: z.string().optional(),
    services: z.array(z.object({
      serviceType: z.string(),
      subServiceTypes: z.array(z.string()).optional()
    }))
  }))
  .handler(async ({ data }) => {
    const board = new SubscriptionsManagementBoard();
    
    // Validate at least email or phone is provided
    if (!data.email && !data.phoneNumber) {
      throw new Error('Either email or phone number must be provided');
    }

    // Validate AU phone number if provided
    if (data.phoneNumber) {
      const auPhoneRegex = /^(\+61|0)[2-478][\d\s]{8}$/;
      if (!auPhoneRegex.test(data.phoneNumber.replace(/\s/g, ''))) {
        throw new Error('Invalid Australian phone number');
      }
    }

    // Map UI sub-service names to exact board dropdown labels
    const subServiceLabelMap: Record<string, string> = {
      // VoIP sub-services
      'VoIP|Hosted PBX': 'VOIP - Hosted PBX',
      'VoIP|Outbound Calling': 'VOIP - Outbound Calling',
      'VoIP|Inbound Calling': 'VOIP - Inbound Calling',
      'VoIP|SMS Services': 'SMS - SMS Services',
      'VoIP|Virtual Fax Services': 'VOIP - Virtual Fax Services',
      'VoIP|Web Portal': 'Web Portal',
      // NBN sub-services
      'NBN|Australian Capital Territory': 'NBN - Australian Capital Territory',
      'NBN|New South Wales': 'NBN - New South Wales',
      'NBN|Northern Territory': 'NBN - Northern Territory',
      'NBN|Queensland': 'NBN - Queensland',
      'NBN|South Australia': 'NBN - South Australia',
      'NBN|Tasmania': 'NBN - Tasmania',
      'NBN|Victoria': 'NBN - Victoria',
      'NBN|Western Australia': 'NBN - Western Australia',
    };

    // Collect all service types and sub-service types
    const newServiceTypes: string[] = [];
    const newSubServiceTypes: string[] = [];

    data.services.forEach(svc => {
      if (!newServiceTypes.includes(svc.serviceType)) {
        newServiceTypes.push(svc.serviceType);
      }
      if (svc.subServiceTypes && svc.subServiceTypes.length > 0) {
        svc.subServiceTypes.forEach(sub => {
          const key = `${svc.serviceType}|${sub}`;
          const label = subServiceLabelMap[key];
          if (label && !newSubServiceTypes.includes(label)) {
            newSubServiceTypes.push(label);
          }
        });
      }
    });

    // Check for existing subscription by email or phone
    let existingSubscription = null;
    
    if (data.email) {
      const emailResults = await board.items()
        .withColumns(['email', 'phoneNumber', 'firstName', 'upstreamServiceTypes', 'subServiceType', 'subscriptionStatus'])
        .where({ email: { contains: data.email } })
        .withPagination({ limit: 1 })
        .execute();
      
      if (emailResults.items.length > 0) {
        existingSubscription = emailResults.items[0];
      }
    }

    // If no match by email and phone is provided, search by phone
    if (!existingSubscription && data.phoneNumber) {
      const phoneResults = await board.items()
        .withColumns(['email', 'phoneNumber', 'firstName', 'upstreamServiceTypes', 'subServiceType', 'subscriptionStatus'])
        .where({ phoneNumber: { contains: data.phoneNumber } })
        .withPagination({ limit: 1 })
        .execute();
      
      if (phoneResults.items.length > 0) {
        existingSubscription = phoneResults.items[0];
      }
    }

    if (existingSubscription) {
      // MERGE new services with existing ones (don't replace)
      const existingServiceTypes = existingSubscription.upstreamServiceTypes ?? [];
      const existingSubServiceTypes = existingSubscription.subServiceType ?? [];

      const mergedServiceTypes = [...new Set([...existingServiceTypes, ...newServiceTypes])];
      const mergedSubServiceTypes = [...new Set([...existingSubServiceTypes, ...newSubServiceTypes])];

      // Update existing subscription with merged services
      const updatedSubscriber = await board.item(existingSubscription.id).update({
        firstName: data.firstName,
        email: data.email || existingSubscription.email,
        phoneNumber: data.phoneNumber ? { phone: data.phoneNumber, country: 'AU' } : existingSubscription.phoneNumber,
        upstreamServiceTypes: mergedServiceTypes,
        subServiceType: mergedSubServiceTypes.length > 0 ? mergedSubServiceTypes : null,
        subscriptionStatus: 'Subscribed'
      })
      .returnColumns(['subscriptionStatus', 'upstreamServiceTypes', 'subServiceType', 'email', 'firstName', 'phoneNumber'])
      .inGroup('topics')
      .execute();

      return { ...updatedSubscriber, isUpdate: true };
    } else {
      // Create new subscriber item in the board
      const newSubscriber = await board.item().create({
        name: `${data.firstName} - ${data.email || data.phoneNumber}`,
        firstName: data.firstName,
        email: data.email || null,
        phoneNumber: data.phoneNumber ? { phone: data.phoneNumber, country: 'AU' } : null,
        upstreamServiceTypes: newServiceTypes,
        subServiceType: newSubServiceTypes.length > 0 ? newSubServiceTypes : null,
        subscriptionStatus: 'Subscribed'
      })
      .returnColumns(['subscriptionStatus', 'upstreamServiceTypes', 'subServiceType', 'email', 'firstName', 'phoneNumber'])
      .inGroup('topics')
      .execute();

      return { ...newSubscriber, isUpdate: false };
    }
  });

export const getServiceTypeOptions = createServerFn({ method: 'GET' })
  .validator(z.object({}).optional())
  .handler(async () => {
    const board = new UpstreamServiceOutagesManagementBoard();
    
    // Query the board's column definitions to get ALL dropdown options,
    // not just ones that appear in existing items
    const result = await board.executeGraphQL(`
      query {
        boards(ids: [5029768932]) {
          columns {
            id
            settings_str
          }
        }
      }
    `);
    
    const columns = result?.data?.boards?.[0]?.columns ?? [];
    
    // Find the Service Type dropdown column (id: dropdown_mm528s18)
    const serviceTypeCol = columns.find((c: { id: string }) => c.id === 'dropdown_mm528s18');
    const subServiceTypeCol = columns.find((c: { id: string }) => c.id === 'dropdown_mm52dnr0');
    
    let serviceTypes: string[] = [];
    let subServiceTypes: string[] = [];
    
    if (serviceTypeCol?.settings_str) {
      try {
        const settings = JSON.parse(serviceTypeCol.settings_str);
        // Dropdown settings have a "labels" object: { "1": { "name": "NBN" }, ... }
        serviceTypes = Object.values(settings.labels ?? {})
          .map((label: unknown) => (label as { name: string }).name)
          .filter(Boolean);
      } catch {
        serviceTypes = [];
      }
    }
    
    if (subServiceTypeCol?.settings_str) {
      try {
        const settings = JSON.parse(subServiceTypeCol.settings_str);
        subServiceTypes = Object.values(settings.labels ?? {})
          .map((label: unknown) => (label as { name: string }).name)
          .filter(Boolean);
      } catch {
        subServiceTypes = [];
      }
    }
    
    return { serviceTypes, subServiceTypes };
  });
